import React, { useState } from 'react';

// Hooks
import { useDispatch } from 'react-redux';

// Actions
import { upgradePlan } from '~/redux/actions/stripe-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';
import { getUserData } from '~/redux/actions/auth-actions';

// Utils
import { balanceFormatter } from '~/utils/helperFunctions';
import { MESSAGES } from '~/utils/message';
import { loadStripe } from '@stripe/stripe-js';
import ConfirmModal from '~/components/General/ConfirmModal';

//MUI Component
import Typography from '@mui/material/Typography';
import { Box } from '@mui/material';

// Image
import { UpgradeCIcon } from '~/assets/images';

// Styles
import './styles.scss';

const getResponsePayload = (response) => response?.data?.data ?? response?.data ?? {};

const planMatchesTarget = (userResponse, targetStripeId) => {
  const subscribedPlan = userResponse?.data?.data?.subscribedPlan;

  return subscribedPlan?.stripeId === targetStripeId;
};

const waitForPlanUpgrade = async (dispatch, targetStripeId, maxAttempts = 5, delayMs = 1000) => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const userResponse = await dispatch(getUserData());

    if (planMatchesTarget(userResponse, targetStripeId)) {
      return true;
    }

    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => {
        setTimeout(resolve, delayMs);
      });
    }
  }

  return false;
};

const UpgradeModal = (props) => {
  const { handleClose, open, selectedPlan, planDetails } = props;
  const [requestLoader, setRequestLoader] = useState(false);

  const dispatch = useDispatch();

  const completeUpgradeIfApplied = async (successMessage) => {
    const upgraded = await waitForPlanUpgrade(dispatch, selectedPlan.stripeId);

    if (!upgraded) {
      dispatch(failure(MESSAGES.ACCOUNT.PLANS.UPGRADE.GENERIC_ERROR));
      return false;
    }

    dispatch(success(successMessage || MESSAGES.ACCOUNT.PLANS.UPGRADE.SUCCESS));
    handleClose();
    return true;
  };

  const handleRequiresAction = async (paymentIntentClientSecret) => {
    if (!paymentIntentClientSecret) {
      dispatch(failure(MESSAGES.ACCOUNT.PLANS.UPGRADE.GENERIC_ERROR));
      return;
    }

    const stripe = await loadStripe(import.meta.env.VITE_APP_STRIPE_API_KEY);

    if (!stripe) {
      dispatch(failure(MESSAGES.ACCOUNT.PLANS.UPGRADE.GENERIC_ERROR));
      return;
    }

    const { error } = await stripe.confirmCardPayment(paymentIntentClientSecret);

    if (error) {
      dispatch(failure(error.message || MESSAGES.ACCOUNT.PLANS.UPGRADE.CARD_DECLINED));
      return;
    }

    await completeUpgradeIfApplied(MESSAGES.ACCOUNT.PLANS.UPGRADE.SUCCESS);
  };

  const onSubmit = async () => {
    try {
      setRequestLoader(true);
      const response = await upgradePlan({ planId: selectedPlan.stripeId });
      const payload = getResponsePayload(response);

      if (response?.status === 402 && payload?.requires_action) {
        await handleRequiresAction(payload.payment_intent_client_secret);
        return;
      }

      if (response?.status === 200) {
        await completeUpgradeIfApplied(
          response?.data?.message || MESSAGES.ACCOUNT.PLANS.UPGRADE.SUCCESS
        );
        return;
      }

      await dispatch(getUserData());

      const errorCode = payload?.error_code;
      const serverMessage = response?.data?.message || payload?.message;

      if (errorCode === 'card_declined' || errorCode === 'payment_error') {
        dispatch(failure(serverMessage || MESSAGES.ACCOUNT.PLANS.UPGRADE.CARD_DECLINED));
      } else if (errorCode === 'no_active_subscription') {
        dispatch(failure(serverMessage || MESSAGES.ACCOUNT.PLANS.UPGRADE.GENERIC_ERROR));
      } else {
        dispatch(failure(serverMessage || MESSAGES.ACCOUNT.PLANS.UPGRADE.GENERIC_ERROR));
      }
    } catch (error) {
      const response = error?.response;
      const payload = getResponsePayload(response);

      if (response?.status === 402 && payload?.requires_action) {
        await handleRequiresAction(payload.payment_intent_client_secret);
        return;
      }

      dispatch(failure(response?.data?.message || MESSAGES.ACCOUNT.PLANS.UPGRADE.GENERIC_ERROR));
    } finally {
      setRequestLoader(false);
    }
  };

  return (
    <ConfirmModal
      openConfirmModal={{
        open,
        header: MESSAGES.ACCOUNT.PLANS.UPGRADE.TITLE,
        icon: UpgradeCIcon,
        className: 'upgradeConfirmModal',
        message: (
          <Box className="upgradePlanSummary">
            <Typography className="upgradePlanName">{selectedPlan.name}</Typography>
            {selectedPlan.amount && (
              <Typography className="upgradePlanPrice">
                {balanceFormatter(selectedPlan.amount)}/{selectedPlan.isYearly ? 'yr' : 'month'}
              </Typography>
            )}
          </Box>
        ),
        bodyContent: (
          <Box className="upgradeDetails">
            <Typography className="upgradeDescription">
              You will be charged {balanceFormatter(planDetails?.cost)} today. Your plan will
              upgrade immediately. Starting on {planDetails?.nextChargeDate || ''} you will be
              billed for {balanceFormatter(selectedPlan.amount)}
              {selectedPlan.isYearly ? ' every year.' : ' every month.'}
            </Typography>
          </Box>
        ),
        loading: requestLoader,
        successButtonName: MESSAGES.ACCOUNT.PLANS.UPGRADE.SUBMIT_BUTTON,
        cancelButtonName: MESSAGES.ACCOUNT.PLANS.UPGRADE.CANCEL_BUTTON,
        handleOk: onSubmit,
        handleClose,
      }}
    />
  );
};

export default UpgradeModal;
